import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Layout } from "../components/Layout";
import { Package, ArrowRight, GithubLogo, MagnifyingGlass, SlidersHorizontal } from "@phosphor-icons/react";

export default function Catalog() {
  const [software, setSoftware] = useState([]);
  const [providers, setProviders] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [query, setQuery] = useState("");
  const [mdmFilter, setMdmFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/software").then((r) => setSoftware(r.data)).catch(() => {});
    api.get("/providers").then((r) => setProviders(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const counts = software.reduce((acc, s) => {
      acc[s.category] = (acc[s.category] || 0) + 1;
      return acc;
    }, {});
    return ["All", ...Object.keys(counts).sort()].map((c) => ({
      name: c,
      count: c === "All" ? software.length : counts[c],
    }));
  }, [software]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return software.filter((s) => {
      if (activeCat !== "All" && s.category !== activeCat) return false;
      if (mdmFilter !== "all" && !s.compat.includes(mdmFilter)) return false;
      if (q) {
        const hay = `${s.name} ${s.desc} ${s.repo} ${s.author} ${s.tags.join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [software, activeCat, query, mdmFilter]);

  const providerName = (id) => providers.find((p) => p.id === id)?.name || id;

  const deploy = (sw) => navigate("/app", { state: { software: sw } });
  const configure = (sw) => navigate("/app/appconfig", { state: { appId: sw.id } });

  return (
    <Layout>
      <div className="border-b border-[#27272A] px-8 py-6">
        <h1 className="text-2xl tracking-tight flex items-center gap-2">
          <Package size={22} className="text-[#00F0FF]" /> Software Catalog
        </h1>
        <p className="text-sm text-[#A1A1AA] font-mono mt-1">
          Plug &amp; play open-source Apple tools. Generate a deployment script in one click.
        </p>
      </div>

      <div className="px-8 pt-6 space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
            <input
              data-testid="catalog-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tools by name, tag or author…"
              className="w-full bg-[#050505] border border-[#27272A] pl-9 pr-3 py-2.5 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200"
            />
          </div>
          <select
            data-testid="mdm-filter"
            value={mdmFilter}
            onChange={(e) => setMdmFilter(e.target.value)}
            className="bg-[#050505] border border-[#27272A] px-3 py-2.5 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200 md:w-56"
          >
            <option value="all">All MDM providers</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                Compatible with {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="category-tabs">
          {categories.map((c) => (
            <button
              key={c.name}
              data-testid={`category-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setActiveCat(c.name)}
              className={`flex items-center gap-2 px-3.5 py-1.5 border font-mono text-xs transition-colors duration-200 ${
                activeCat === c.name
                  ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/5"
                  : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46] hover:text-[#FAFAFA]"
              }`}
            >
              {c.name}
              <span className={activeCat === c.name ? "text-[#00F0FF]" : "text-[#52525b]"}>{c.count}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="p-8">
        {filtered.length === 0 ? (
          <div className="border border-dashed border-[#27272A] py-20 text-center" data-testid="catalog-empty">
            <Package size={40} className="text-[#27272A] mx-auto mb-4" />
            <p className="text-sm text-[#71717A] font-mono">No tools match your filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((sw) => (
            <div
              key={sw.id}
              data-testid={`software-card-${sw.id}`}
              className="border border-[#27272A] bg-[#121214] p-6 flex flex-col hover:border-[#3f3f46] transition-colors duration-200"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 border border-[#27272A] flex items-center justify-center">
                  <Package size={20} className="text-[#00F0FF]" />
                </div>
                <span className="font-mono text-[10px] text-[#71717A] border border-[#27272A] px-2 py-1">
                  {sw.category}
                </span>
              </div>

              <h3 className="text-lg tracking-tight">{sw.name}</h3>
              <a
                href={sw.url}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-[11px] text-[#71717A] hover:text-[#00F0FF] transition-colors duration-200 flex items-center gap-1 mb-3"
              >
                <GithubLogo size={12} /> {sw.repo}
              </a>

              <p className="text-sm text-[#A1A1AA] leading-relaxed flex-1">{sw.desc}</p>

              <div className="flex flex-wrap gap-1.5 mt-4 mb-4">
                {sw.tags.map((t) => (
                  <span key={t} className="font-mono text-[10px] text-[#00F0FF] bg-[#00F0FF]/10 px-2 py-0.5">
                    {t}
                  </span>
                ))}
              </div>

              <div className="font-mono text-[10px] text-[#71717A] mb-4">
                Compatible: {sw.compat.map(providerName).join(" · ")}
              </div>

              <div className="flex flex-col gap-2">
                <button
                  data-testid={`configure-${sw.id}`}
                  onClick={() => configure(sw)}
                  className="w-full flex items-center justify-center gap-2 bg-[#00F0FF] text-[#0A0A0B] font-medium py-2.5 text-sm hover:bg-[#00d4e0] transition-colors duration-200"
                >
                  <SlidersHorizontal size={16} /> Configure visually
                </button>
                <button
                  data-testid={`deploy-${sw.id}`}
                  onClick={() => deploy(sw)}
                  className="w-full flex items-center justify-center gap-2 border border-[#27272A] text-[#A1A1AA] py-2.5 text-sm hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors duration-200"
                >
                  AI deploy script <ArrowRight size={16} />
                </button>
              </div>
            </div>
          ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
